import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";

export type ButtonVariant = "primary" | "house" | "reads" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Square, icon-only button at the same height as its text siblings. */
  iconOnly?: boolean;
  block?: boolean;
  className?: string;
}

/**
 * The button's classes, for elements that must look like a button but are not
 * one - a real `<a>` for navigation or a file download, where the browser has to
 * handle the request itself.
 */
export function buttonClass({
  variant = "secondary",
  size = "md",
  iconOnly = false,
  block = false,
  className,
}: ButtonClassOptions = {}): string {
  return cx(
    "ui-btn",
    variant !== "secondary" && `ui-btn--${variant}`,
    size !== "md" && `ui-btn--${size}`,
    iconOnly && "ui-btn--icon",
    block && "ui-btn--block",
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<ButtonClassOptions, "className"> {
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  /** Leading icon (any SVG element). Replaced by the spinner while loading. */
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    iconOnly = false,
    block = false,
    loading = false,
    icon,
    iconRight,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // A busy button is disabled and says so, rather than swallowing a second click.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass({ variant, size, iconOnly, block, className })}
      {...rest}
    >
      {loading ? <span className="ui-spinner" aria-hidden="true" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon-only control has no visible text to name it. */
  label: string;
  size?: "sm" | "md";
  /** Transparent at rest; for dense toolbars and close buttons. */
  ghost?: boolean;
  children: ReactNode;
}

/** Chrome action (header, toolbar, close). Not a call to action. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = "md", ghost = false, className, children, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx("ui-icon-btn", size === "sm" && "ui-icon-btn--sm", ghost && "ui-icon-btn--ghost", className)}
      {...rest}
    >
      {children}
    </button>
  );
});
